import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { UnitService, PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import { Unit, UnitDetail, UnitCreate, UnitUpdate, Resident, ResidentCreate, ResidentUpdate, UnitBulkImportPayload, BulkImportResult } from '@core/types/unit.type';
import { QUERY_KEYS } from '@core/constans';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';
import { environment } from '@environments/environment';

/**
 * Query service para operaciones de Units y Residents
 * Utiliza TanStack Query para caché inteligente, mutations y sincronización automática
 */
@Injectable({ providedIn: 'root' })
export class UnitQuery {
  private readonly unitService = inject(UnitService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  // ============ UNIT QUERIES ============

  /**
   * Query para obtener lista paginada de unidades
   * @param config - Configuración de paginación y filtros
   */
  useUnitsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const currentConfig = config();

      return {
        queryKey: [...QUERY_KEYS.units.grid(currentConfig), condominiumId],
        queryFn: () => lastValueFrom(this.unitService.getAll(currentConfig)),
        select: (data: ApiResponse<PaginationResult<Unit>>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  /**
   * Query para obtener detalle de una unidad con sus residentes
   * @param unitId - ID de la unidad
   */
  useUnitDetail(unitId: () => string | null) {
    return injectQuery(() => {
      const id = unitId();
      return {
        queryKey: QUERY_KEYS.units.detail(id || ''),
        queryFn: () => lastValueFrom(this.unitService.getById(id!)),
        select: (data: ApiResponse<UnitDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  /**
   * Query para obtener residentes activos de una unidad
   * @param unitId - ID de la unidad
   */
  useUnitResidents(unitId: () => string | null) {
    return injectQuery(() => {
      const id = unitId();
      return {
        queryKey: QUERY_KEYS.units.residents(id || ''),
        queryFn: () => lastValueFrom(this.unitService.getResidents(id!)),
        select: (data: ApiResponse<Resident[]>) => data.data || [],
        enabled: !!id,
      };
    });
  }

  /**
   * Query para obtener historial de residentes de una unidad
   * @param unitId - ID de la unidad
   */
  useResidentHistory(unitId: () => string | null) {
    return injectQuery(() => {
      const id = unitId();
      return {
        queryKey: QUERY_KEYS.units.residentHistory(id || ''),
        queryFn: () => lastValueFrom(this.unitService.getResidentHistory(id!)),
        select: (data: ApiResponse<Resident[]>) => data.data || [],
        enabled: !!id,
      };
    });
  }

  // ============ UNIT MUTATIONS ============

  /**
   * Mutation para crear una nueva unidad
   * Invalida la lista de unidades al completarse
   */
  useCreateUnit() {
    return injectMutation(() => ({
      mutationFn: (data: UnitCreate) =>
        lastValueFrom(this.unitService.create(data)),
      onSuccess: () => {
        // Invalidar todas las queries de unidades para refetch automático
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.all
        });
        // También invalidar el detalle del condominio (tiene zonas con unidades)
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.condominiums.details()
        });
      },
    }));
  }

  /**
   * Mutation para actualizar una unidad existente
   * Invalida la unidad específica y la lista general
   */
  useUpdateUnit() {
    return injectMutation(() => ({
      mutationFn: (data: UnitUpdate) =>
        lastValueFrom(this.unitService.update(data)),
      onSuccess: (_result, variables) => {
        // Invalidar la unidad específica
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.detail(variables.id)
        });
        // Invalidar la lista
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.all
        });
      },
    }));
  }

  /**
   * Mutation para eliminar una unidad
   * Invalida todas las queries de unidades
   */
  useDeleteUnit() {
    return injectMutation(() => ({
      mutationFn: (id: string) =>
        lastValueFrom(this.unitService.delete(id)),
      onSuccess: () => {
        // Invalidar todas las queries de unidades
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.all
        });
      },
    }));
  }

  // ============ RESIDENT MUTATIONS ============

  /**
   * Mutation para agregar un residente a una unidad
   */
  useAddResident() {
    return injectMutation(() => ({
      mutationFn: (params: { unitId: string; resident: Omit<ResidentCreate, 'unitId'> }) =>
        lastValueFrom(this.unitService.addResident(params.unitId, params.resident)),
      onSuccess: (_result, variables) => {
        // Invalidar residentes de la unidad
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.residents(variables.unitId)
        });
        // Invalidar detalle de la unidad
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.detail(variables.unitId)
        });
        // Invalidar lista de unidades (para actualizar contador de residentes)
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.all
        });
      },
    }));
  }

  /**
   * Mutation para actualizar un residente
   */
  useUpdateResident() {
    return injectMutation(() => ({
      mutationFn: (params: {
        unitId: string;
        residentId: string;
        resident: Omit<ResidentUpdate, 'unitId' | 'id'>
      }) =>
        lastValueFrom(this.unitService.updateResident(
          params.unitId,
          params.residentId,
          params.resident
        )),
      onSuccess: (_result, variables) => {
        // Invalidar residentes de la unidad
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.residents(variables.unitId)
        });
        // Invalidar historial
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.residentHistory(variables.unitId)
        });
        // Invalidar detalle de la unidad
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.detail(variables.unitId)
        });
      },
    }));
  }

  /**
   * Mutation para importar unidades masivamente desde Excel
   * Invalida todas las queries de unidades y condominios al completarse
   */
  useBulkImportUnits() {
    return injectMutation(() => ({
      mutationFn: (payload: UnitBulkImportPayload) =>
        lastValueFrom(this.unitService.bulkImport(payload)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.units.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.condominiums.details() });
      },
    }));
  }

  /**
   * Mutation para remover un residente de una unidad
   */
  useRemoveResident() {
    return injectMutation(() => ({
      mutationFn: (params: { unitId: string; residentId: string }) =>
        lastValueFrom(this.unitService.removeResident(params.unitId, params.residentId)),
      onSuccess: (_result, variables) => {
        // Invalidar residentes de la unidad
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.residents(variables.unitId)
        });
        // Invalidar historial (el residente pasa a inactivo)
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.residentHistory(variables.unitId)
        });
        // Invalidar detalle y lista
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.detail(variables.unitId)
        });
        this.queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.units.all
        });
      },
    }));
  }
}
